let drawerRuntime;

function ownedElement(review, selector) {
  const element = review.querySelector(selector);
  return element?.closest('.jdgm-rev') === review ? element : null;
}

function verifiedBadge(label) {
  const badge = document.createElement('span');
  badge.className = 'tfp-review-verified-badge';

  const icon = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  icon.setAttribute('viewBox', '0 0 16 16');
  icon.setAttribute('aria-hidden', 'true');
  icon.classList.add('tfp-review-verified-badge__icon');

  const circle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
  circle.setAttribute('cx', '8');
  circle.setAttribute('cy', '8');
  circle.setAttribute('r', '7');

  const check = document.createElementNS('http://www.w3.org/2000/svg', 'path');
  check.setAttribute('d', 'm4.7 8.1 2.1 2.1 4.5-4.6');

  icon.append(circle, check);
  badge.append(icon, document.createTextNode(label));
  return badge;
}

function formattedDate(value) {
  const isoDate = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (isoDate) {
    const year = Number(isoDate[1]);
    const month = Number(isoDate[2]);
    const day = Number(isoDate[3]);
    const date = new Date(Date.UTC(year, month - 1, day));
    if (date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day) {
      return `${isoDate[3]}/${isoDate[2]}/${isoDate[1]}`;
    }
    return '';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  return `${day}/${month}/${date.getFullYear()}`;
}

function enhanceProviderReviews(root = document) {
  const wrappers = [];
  if (root instanceof Element && root.matches('[data-tfp-provider-reviews]')) wrappers.push(root);
  wrappers.push(...root.querySelectorAll('[data-tfp-provider-reviews]'));

  for (const wrapper of wrappers) {
    const label = wrapper.dataset.verifiedLabel?.trim();

    for (const review of wrapper.querySelectorAll('.jdgm-rev')) {
      const reply = ownedElement(review, '.jdgm-rev__reply');
      if (reply && !reply.textContent.trim() && !reply.querySelector('img, video, audio, iframe')) reply.hidden = true;
      if (review.dataset.verifiedBuyer === 'true' && label && !ownedElement(review, '.tfp-review-verified-badge')) {
        const author = ownedElement(review, '.jdgm-rev__author');
        const badgeTarget = author?.parentElement || ownedElement(review, '.jdgm-rev__header');
        badgeTarget?.append(verifiedBadge(label));
      }

      for (const time of review.querySelectorAll('time[datetime]')) {
        if (time.closest('.jdgm-rev') !== review) continue;
        if (time.textContent.trim()) continue;
        const date = formattedDate(time.dateTime);
        if (date) time.textContent = date;
      }

      for (const image of review.querySelectorAll('img[data-src]')) {
        if (image.closest('.jdgm-rev') !== review) continue;
        const source = image.dataset.src?.trim();
        if (source) image.setAttribute('src', source);
        image.loading = 'lazy';
        image.decoding = 'async';
      }
    }
  }
}

function reviewDetailsFor(target) {
  if (!(target instanceof Element)) return null;
  const details = document.getElementById('tfp-product-reviews');
  if (!(details instanceof HTMLDetailsElement)) return null;
  const summary = details.querySelector(':scope > summary');
  const isSummary = target.closest('summary') === summary;
  const isReviewLink = Boolean(target.closest('a[data-accordion-target="tfp-product-reviews"]'));
  return isSummary || isReviewLink ? details : null;
}

function loadDrawerRuntime() {
  drawerRuntime ||= import('@theme/theme-drawer').catch((error) => {
    drawerRuntime = undefined;
    throw error;
  });
  return drawerRuntime;
}

async function openReviews(details) {
  const drawer = document.getElementById(details.dataset.tfpReviewDrawerId || '');
  if (!(drawer instanceof HTMLElement)) {
    details.open = true;
    return;
  }

  try {
    await loadDrawerRuntime();
    if (!drawer.isConnected) return;
    customElements.upgrade(drawer);
    await drawer.open();
  } catch (error) {
    console.warn('[tfp-reviews] Drawer unavailable.', error);
    details.open = true;
    details.scrollIntoView({ block: 'nearest' });
  }
}

async function loadReviews(target) {
  if (!(target instanceof HTMLElement) || target.dataset.loaded === 'true' || target.dataset.loading === 'true') return;
  const sectionId = target.dataset.sectionId;
  if (!sectionId) return;

  target.dataset.loading = 'true';
  target.setAttribute('aria-busy', 'true');
  const retry = target.querySelector('[data-tfp-reviews-retry]');
  if (retry instanceof HTMLElement) retry.hidden = true;

  try {
    const url = new URL(window.location.href);
    url.hash = '';
    url.searchParams.set('section_id', sectionId);
    const response = await fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
    if (!response.ok) throw new Error(String(response.status));

    const documentFragment = new DOMParser().parseFromString(await response.text(), 'text/html');
    const content = documentFragment.querySelector('[data-tfp-native-reviews]');
    if (!(content instanceof HTMLElement)) throw new Error('Review content unavailable');
    if (!target.isConnected) return;

    enhanceProviderReviews(content);
    target.replaceChildren(content);
    target.dataset.loaded = 'true';
    if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      content.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 240, easing: 'ease-out' });
    }
  } catch (error) {
    console.warn('[tfp-reviews] Reviews could not be loaded.', error);
    if (retry instanceof HTMLElement && target.isConnected) retry.hidden = false;
  } finally {
    if (target.isConnected) {
      target.dataset.loading = 'false';
      target.setAttribute('aria-busy', 'false');
    }
  }
}

document.addEventListener('pointerdown', (event) => {
  if (reviewDetailsFor(event.target)) void loadDrawerRuntime().catch(() => {});
}, { capture: true, passive: true });

document.addEventListener('focusin', (event) => {
  if (reviewDetailsFor(event.target)) void loadDrawerRuntime().catch(() => {});
}, true);

document.addEventListener('click', (event) => {
  const target = event.target;
  if (!(target instanceof Element)) return;

  const retry = target.closest('[data-tfp-reviews-retry]');
  if (retry) {
    void loadReviews(retry.closest('[data-tfp-reviews-lazy]'));
    return;
  }

  const details = reviewDetailsFor(target);
  if (!details) return;
  event.preventDefault();
  event.stopPropagation();
  void openReviews(details);
}, true);

document.addEventListener('tfp:information-tab-selected', (event) => {
  if (event.detail?.value !== 'reviews') return;
  void loadReviews(event.target.querySelector('[data-information-panel="reviews"] [data-tfp-reviews-lazy]'));
});

// The tabs may have selected a deep-linked panel before this module was ready.
for (const target of document.querySelectorAll('tfp-product-information-tabs[data-enhanced] [data-information-panel="reviews"]:not([hidden]) [data-tfp-reviews-lazy]')) {
  void loadReviews(target);
}

document.addEventListener('theme-drawer:open', (event) => {
  const drawer = event.target;
  if (!(drawer instanceof HTMLElement) || !drawer.hasAttribute('data-tfp-reviews-drawer')) return;
  void loadReviews(drawer.querySelector('[data-tfp-reviews-lazy]'));
});

if (window.location.hash === '#tfp-product-reviews') {
  queueMicrotask(() => {
    const details = document.getElementById('tfp-product-reviews');
    if (details instanceof HTMLDetailsElement) void openReviews(details);
  });
}
